import { z } from "zod";

/**
 * WHO IS TRAINING, ON THE WIRE — the four axes the engine keeps deliberately independent, plus
 * the restrictions that ride on a request rather than on the profile, plus what the athlete
 * themself asked for.
 *
 * MIRRORS SEVEN TABLES, ONE SCHEMA EACH:
 *   `db/schemas/tables/athlete_body.sql`          → `athleteBodySchema`
 *   `db/schemas/tables/athlete_ladders.sql`       → `athleteLadderSchema`
 *   `db/schemas/tables/athlete_limits.sql`        → `athleteLimitSchema`
 *   `db/schemas/tables/athlete_skills.sql`        → `athleteSkillSchema`
 *   `db/schemas/tables/athlete_restrictions.sql`  → `athleteRestrictionSchema`
 *   `db/schemas/tables/athlete_wishes.sql`        → `athleteWishSchema`
 *   `db/schemas/tables/athlete_shape_filter.sql`  → `athleteShapeFilterSchema`
 *
 * THE ENUMS BELOW ARE THE TABLES' `check` CONSTRAINTS, RESTATED SO THE APP CAN REFUSE FIRST.
 * That is the same argument `media.ts` makes for its mime-type allowlist: a constraint only
 * the database knows costs a round trip to discover, and on a phone that is seconds. Where a
 * column carries NO check — `ladder`, `movement`, `body_part` — the identifier is an OPEN
 * alias in the engine (`LadderId`, `MovementId`, `BodyPartId`) whose registry a coach may
 * extend without a deploy, so this file must not close it either. A contract that is stricter
 * than the column silently deletes rows the database is happy to hold.
 *
 * NOTHING HERE IS A `Fact<T>` OBJECT. The engine nests value + source + observedAt; the
 * tables flatten it into three columns, and so does the wire. One less nesting level for a
 * shape the app only ever renders flat, and the feature's converter is where the two meet.
 */

/**
 * `FactSource`, in the engine's own confidence order. Only a row the athlete or the app
 * actually produced is ever stored — `derived` and `assumed` are computed at request time
 * and never written back — but they are on the wire because a READ can hand back a value the
 * engine substituted, and "we assumed this" has to be sayable.
 */
/**
 * 🚨 `estimated` — A MAX COMPUTED FROM A SET THE ATHLETE ACTUALLY LOGGED.
 *
 * ⚠️ THIS LIST IS THE WIRE'S COPY OF THE ENGINE'S OWN `FactSource` UNION and the two are asserted
 * equal in both directions. It was spelled out a second and third time inside `sessions.ts` as a
 * bare `z.enum([...])`, so adding a source to the engine left two literals behind that no test
 * compared against anything — and the first session priced off a logged set came back as a 500,
 * because the contract parse on the way out refused a value the engine had just produced. Those
 * two literals now read this constant. One list.
 */
export const FACT_SOURCES = [
  "measured",
  "reported",
  "estimated",
  "derived",
  "assumed",
] as const;
export const factSourceSchema = z.enum(FACT_SOURCES);
export type FactSource = (typeof FACT_SOURCES)[number];

/** `SelfRatedLevel` — the cheapest fact in the system, and the only one available at signup. */
export const SELF_RATED_LEVELS = ["beginner", "intermediate", "advanced"] as const;
export const selfRatedLevelSchema = z.enum(SELF_RATED_LEVELS);
export type SelfRatedLevel = (typeof SELF_RATED_LEVELS)[number];

/**
 * OURS, NOT THE ENGINE'S. `AthleteProfile` has no `sex` field and no rule under
 * `api/src/engine/` reads one — the strength model runs off `level`, `bodyMass` and the
 * recorded maxes. It is stored because the product asked for it, so the value set is this
 * codebase's own and matches `athlete_body.sex`'s check exactly.
 */
export const SEXES = ["female", "male", "other", "prefer-not-to-say"] as const;
export const sexSchema = z.enum(SEXES);
export type Sex = (typeof SEXES)[number];

/**
 * ONE ATHLETE, ONE BODY ON THE WIRE — AND A LOG UNDERNEATH (BUD-297). `athlete_body` used
 * to be `unique (user_id)`, which meant a new weigh-in destroyed the previous one; it is now
 * append-only and the current athlete is its latest row. That is deliberately invisible here:
 * this schema is still one body and not a list, there is still no id param and the caller is
 * still the key, because the defect was about what the database KEEPS and not about what a
 * screen reads. A history endpoint, if it is ever wanted, is a separate resource.
 *
 * `bodyMassKg` AND `bodyMassSource` TRAVEL TOGETHER OR NOT AT ALL, which the table states as
 * `check ((body_mass_kg is null) = (body_mass_source is null))`. A number with no provenance
 * is a number a fitting job cannot price. Expressed here as a refinement rather than as two
 * independently nullable fields, so the app cannot compose a half-answer and discover it at
 * the database.
 */
export const athleteBodySchema = z
  .object({
    sex: sexSchema.nullable(),
    bodyMassKg: z.number().positive().nullable(),
    bodyMassSource: factSourceSchema.nullable(),
    /** `Fact.observedAt`. A civil-calendar date — the engine ages a fact by days and reads no clock. */
    bodyMassObservedAt: z.iso.date().nullable(),
    /**
     * `capability/floor.ts` calls this "the one field with no safe absence": without it
     * `maxFor` reaches the by-feel rung for every barbell movement and `eligibleForSlot` then
     * drops a by-feel bar from every pool, so an unknown athlete in a fully equipped box is
     * offered no barbell at all. Nullable because a fresh signup genuinely has not been
     * asked; the engine substitutes `beginner`, and the receipt says it did.
     */
    level: selfRatedLevelSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .refine((v) => (v.bodyMassKg === null) === (v.bodyMassSource === null), {
    message: "body-mass-needs-a-source",
    path: ["bodyMassSource"],
  });

export type AthleteBodyDTO = z.infer<typeof athleteBodySchema>;

/** Every field is optional: this screen is a form somebody fills in over weeks, not at once. */
export const updateAthleteBodySchema = z
  .object({
    sex: sexSchema.nullable(),
    bodyMassKg: z.number().positive().max(500).nullable(),
    bodyMassSource: factSourceSchema.nullable(),
    bodyMassObservedAt: z.iso.date().nullable(),
    level: selfRatedLevelSchema.nullable(),
  })
  .partial()
  /**
   * THE PAIR TRAVELS BY VALUE, NOT BY KEY. `athlete_body` declares
   * `check ((body_mass_kg is null) = (body_mass_source is null))` — a check on the VALUES —
   * and `athleteBodySchema` above refines on exactly that. This one used to ask
   * `("bodyMassKg" in v) === ("bodyMassSource" in v)`, which under `.partial()` is a
   * different question: `in` tests KEY PRESENCE, so `{ bodyMassKg: 80, bodyMassSource: null }`
   * named both keys, passed, reached the table and came back to the app as a `500` it could
   * not mark an input with (BUD-302).
   *
   * Under `.partial()` "absent" and "explicitly null" are genuinely different, and both have
   * to behave, so the rule is stated in two parts:
   *
   * 1. NAMING ONE NAMES BOTH. The patch writes whichever columns it names, so a patch that
   *    sets a mass without a source would leave the stored source as it was and reach the
   *    same check. Absent means "not answered today" only for the PAIR, never for half of it.
   * 2. ONCE BOTH ARE NAMED, THEY ARE NULL TOGETHER OR FILLED TOGETHER. A weight with no
   *    provenance is a number a fitting job cannot price; both null is the pair being
   *    cleared, which is legal.
   *
   * `undefined` counts as absent rather than as a value: a JSON body cannot carry one, and
   * treating it as "named" would let `{ bodyMassKg: 80, bodyMassSource: undefined }` through
   * to a handler that writes the source as null.
   */
  .refine(
    (v) => {
      const massNamed = v.bodyMassKg !== undefined;
      const sourceNamed = v.bodyMassSource !== undefined;
      if (!massNamed && !sourceNamed) return true;
      if (!massNamed || !sourceNamed) return false;
      return (v.bodyMassKg === null) === (v.bodyMassSource === null);
    },
    { message: "body-mass-needs-a-source", path: ["bodyMassSource"] },
  );

export type UpdateAthleteBodyPayload = z.infer<typeof updateAthleteBodySchema>;

/**
 * CAN I DO IT AT ALL — `athlete_ladders`. A rung is a PLAIN NUMBER and not a `Fact`, which is
 * the engine's own shape: it is a position the athlete tapped on a ladder they were shown, so
 * there is nothing to age and no confidence to decay.
 */
export const athleteLadderSchema = z.object({
  id: z.uuid(),
  /** A `LadderId`. Open alias — the registry adds rungs without a migration, so no enum here. */
  ladder: z.string().min(1),
  /** Rung zero is the bottom of every ladder and is what the population floor assumes. */
  rung: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteLadderDTO = z.infer<typeof athleteLadderSchema>;

/** An upsert, matching the table's `unique (user_id, ladder)`: setting a rung twice is setting it once. */
export const setAthleteLadderSchema = z.object({
  ladder: z.string().min(1).max(80),
  rung: z.number().int().min(0).max(50),
});

export type SetAthleteLadderPayload = z.infer<typeof setAthleteLadderSchema>;

/**
 * HOW HEAVY — `athlete_limits`. Keyed by MOVEMENT and not by "lift", because the engine keys
 * `maxes` and `unbroken` by the same `MovementId` and derives an unrecorded max from a
 * recorded one through a chain of movement ids. Two names for one identifier is a defect the
 * engine's own comments spend three paragraphs on.
 */
export const athleteLimitSchema = z.object({
  id: z.uuid(),
  /** A `MovementId`. Tier B content a coach may extend without a deploy — open, no enum. */
  movement: z.string().min(1),
  oneRepMaxKg: z.number().positive(),
  source: factSourceSchema,
  /** `Fact.observedAt`. Nullable: an honest absence for anything the athlete did not date. */
  observedAt: z.iso.date().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteLimitDTO = z.infer<typeof athleteLimitSchema>;

export const setAthleteLimitSchema = z.object({
  movement: z.string().min(1).max(120),
  oneRepMaxKg: z.number().positive().max(600),
  /**
   * Only what the athlete or the app produced is stored. `derived` and `assumed` are the
   * engine's own substitutions and it computes them per request, so accepting one here would
   * let a client write a guess into the place a measurement belongs.
   */
  source: z.enum(["measured", "reported"]),
  observedAt: z.iso.date().nullable().optional(),
});

export type SetAthleteLimitPayload = z.infer<typeof setAthleteLimitSchema>;

/**
 * HOW MANY IN A ROW — `athlete_skills`. The only input in the system with no passive source:
 * a one-rep max derives from another lift, but how many pull-ups you can string together
 * derives from nothing.
 *
 * ZERO IS A REAL MEASUREMENT and an absent row is not. `maxUnbroken` is `>= 0` because a
 * stored zero means "cannot do one"; "unmeasured" is the row not existing.
 */
export const athleteSkillSchema = z.object({
  id: z.uuid(),
  movement: z.string().min(1),
  maxUnbroken: z.number().int().min(0),
  source: factSourceSchema,
  observedAt: z.iso.date().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteSkillDTO = z.infer<typeof athleteSkillSchema>;

export const setAthleteSkillSchema = z.object({
  movement: z.string().min(1).max(120),
  maxUnbroken: z.number().int().min(0).max(1000),
  source: z.enum(["measured", "reported"]),
  observedAt: z.iso.date().nullable().optional(),
});

export type SetAthleteSkillPayload = z.infer<typeof setAthleteSkillSchema>;

/**
 * MUST NOT TODAY, WHICH IS NOT CANNOT YET — `athlete_restrictions`.
 *
 * `RestrictionDegree` IS THREE ENFORCEMENT MECHANISMS AND NOT THREE SEVERITIES:
 *   avoid       — do not give them this at all.
 *   limit-load  — allowed, not heavy. Capped, and barred from the strength block, because a
 *                 percentage of a one-rep max IS the load.
 *   limit-range — allowed, not through the extreme range.
 */
export const RESTRICTION_DEGREES = ["avoid", "limit-load", "limit-range"] as const;
export const restrictionDegreeSchema = z.enum(RESTRICTION_DEGREES);
export type RestrictionDegree = (typeof RESTRICTION_DEGREES)[number];

export const RESTRICTION_KINDS = ["body", "movement", "impact"] as const;

/**
 * THE TARGET CARRIES ITS KIND, AND THAT IS THE WHOLE POINT.
 *
 * A DISCRIMINATED UNION RATHER THAN THREE NULLABLE COLUMNS, which is exactly what the table's
 * three-armed `check` constraint buys and what a flattened `body_part` would throw away: an
 * impact restriction names no body part at all, and a nullable `bodyPart` with no
 * discriminator cannot tell "no jumping" from a row somebody forgot to fill in.
 *
 * A bad knee is not a quads restriction — a back squat is fine and a box jump is not, and the
 * axis that separates them is LANDING, not which muscle does the work.
 */
export const restrictionTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("body"), bodyPart: z.string().min(1) }),
  z.object({ kind: z.literal("movement"), movement: z.string().min(1) }),
  z.object({ kind: z.literal("impact") }),
]);

export type RestrictionTarget = z.infer<typeof restrictionTargetSchema>;

export const athleteRestrictionSchema = z.object({
  id: z.uuid(),
  target: restrictionTargetSchema,
  degree: restrictionDegreeSchema,
  /**
   * THE LAST DAY IT APPLIES, compared against the session's date. Null means permanent, which
   * is a real thing and has to be sayable. An expired row is NOT deleted: the engine reports
   * it on the receipt as `restriction.expired`, because a restriction that stopped applying
   * is exactly what an athlete wants to be told before the movement comes back.
   */
  until: z.iso.date().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteRestrictionDTO = z.infer<typeof athleteRestrictionSchema>;

export const createAthleteRestrictionSchema = z.object({
  target: restrictionTargetSchema,
  degree: restrictionDegreeSchema,
  until: z.iso.date().nullable().optional(),
});

export type CreateAthleteRestrictionPayload = z.infer<typeof createAthleteRestrictionSchema>;

/**
 * WHAT THE ATHLETE THEMSELF ASKED FOR — `athlete_wishes` (BUD-334).
 *
 * TWO STANCES AND A THIRD STATE THAT IS NO ROW. Frame `9c` is a tri-state movement filter: tap
 * once to want, twice to avoid, a third time to clear. `unique (user_id, movement)` on the table
 * is what makes the first two an upsert; the third is a delete.
 *
 * WANT IS SOFT AND AVOID IS HARD, and they are two mechanisms rather than two severities — the
 * same distinction `RESTRICTION_DEGREES` draws:
 *   want   — a weight on the draw. It makes the movement likelier and can never make it
 *            compulsory, because a want that can starve a session is an avoid of everything else
 *            wearing the wrong name. It is also CAPPED: once the movement has already come up
 *            twice in seven days the want stops pushing, so a wish set in March cannot quietly
 *            buy the same movement every session for ever.
 *   avoid  — the movement is gone from the draw's catalog before any rule sees it, and it reaches
 *            the engine on the RESTRICTION channel, which is the one channel no relaxation can
 *            lift.
 */
export const WISH_STANCES = ["want", "avoid"] as const;
export const wishStanceSchema = z.enum(WISH_STANCES);
export type WishStance = (typeof WISH_STANCES)[number];

export const athleteWishSchema = z.object({
  id: z.uuid(),
  /** A `MovementId`. Open alias — Tier B content a coach may extend without a deploy. */
  movement: z.string().min(1),
  stance: wishStanceSchema,
  /**
   * THE DAY THE APP MAY ASK ABOUT THIS WISH, AND NOT THE DAY IT STOPS APPLYING. A wish persists
   * until the athlete changes it (`### Decided` item 2); this is the coaching rule that came with
   * it — a wish set once and never revisited is how somebody programmes themselves into a corner,
   * so on this date the app is entitled to ask ONCE, and the athlete's answer is what changes it.
   * Nothing in the service acts on it: the asking is the client's.
   */
  reviewAt: z.iso.date(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteWishDTO = z.infer<typeof athleteWishSchema>;

/** An upsert on `unique (user_id, movement)`: setting a stance twice sets it once. */
export const setAthleteWishSchema = z.object({
  movement: z.string().min(1).max(120),
  stance: wishStanceSchema,
});

export type SetAthleteWishPayload = z.infer<typeof setAthleteWishSchema>;

/**
 * THE SHAPES THE ATHLETE NARROWED THE DRAW TO — `athlete_shape_filter` (BUD-334).
 *
 * AN EMPTY LIST IS EVERY SHAPE. That is the `Any shape` pill on the deck, and it is the absence of
 * rows rather than a sentinel value: a sentinel is a second thing "no rows" could mean and one
 * more state every reader has to handle.
 */
export const athleteShapeFilterSchema = z.object({
  id: z.uuid(),
  /** A `ShapeId` from the library — see `GET /v1/shapes` for the thirty. Open, no enum. */
  shape: z.string().min(1),
  /** As on a movement wish: the day the app may ask, never the day it stops applying. */
  reviewAt: z.iso.date(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type AthleteShapeFilterDTO = z.infer<typeof athleteShapeFilterSchema>;

/**
 * THE WHOLE FILTER AT ONCE, REPLACING WHAT WAS THERE — not one shape appended.
 *
 * "Narrow the deck to these" is ONE decision, and sending it as one write is what makes it
 * impossible to observe half of it: an athlete who swaps chipper for ladder through two calls has
 * a moment where both are in force and the deck answers from a filter nobody chose. The cap of
 * thirty is the library's own size, which is also the point at which the filter has stopped
 * filtering.
 */
export const setAthleteShapeFilterSchema = z.object({
  shapes: z.array(z.string().min(1).max(120)).max(30),
});

export type SetAthleteShapeFilterPayload = z.infer<typeof setAthleteShapeFilterSchema>;

/**
 * THE FILTER AS IT NOW STANDS — what the write above answers with, so the client never has to
 * re-read the profile to find out what it just set. Declared as its own schema rather than
 * assembled at the call site because a response body is a contract parse, and an array of parsed
 * rows is not itself one.
 */
export const athleteShapeFilterListSchema = z.array(athleteShapeFilterSchema);

export type AthleteShapeFilterListDTO = z.infer<typeof athleteShapeFilterListSchema>;

/**
 * WHAT AN AVOID WOULD COST, ASKED BEFORE IT IS SET (BUD-334).
 *
 * The movement filter never pre-emptively refuses an avoid (`### Decided` item 1) — the athlete
 * may always ban whatever they like. What it must not do is let them do it silently: if this
 * avoid takes the LAST movement in their gym that serves a functional pattern, the app says which
 * pattern, and the athlete decides anyway.
 *
 * A QUESTION ABOUT THE ROOM THEY TRAIN IN, which is why it is a read against the athlete rather
 * than a fact about the movement. "No hinge left" is only true of a gym that held one.
 */
export const wishCostQuerySchema = z.object({
  movement: z.string().min(1).max(120),
});

export type WishCostQuery = z.infer<typeof wishCostQuerySchema>;

export const wishCostSchema = z.object({
  movement: z.string().min(1),
  /**
   * THE PATTERNS THIS AVOID WOULD EMPTY — pattern ids (`hinge`, `squat`, `press`, `pull`, `core`),
   * or an empty list, which is the normal answer and means the avoid costs nothing structural.
   *
   * ⚠️ EMPTY IS ALSO THE ANSWER FOR AN ATHLETE WITH NO GYM ON FILE. There is no room to be the
   * last movement in, so nothing can be emptied; the app should not read an empty list as
   * "checked and safe" in that case. `roomKnown` says which of the two it is.
   */
  emptiedPatterns: z.array(z.string().min(1)),
  /** False when this athlete has named no gym — see `emptiedPatterns`. */
  roomKnown: z.boolean(),
});

export type WishCostDTO = z.infer<typeof wishCostSchema>;

/**
 * ═══ 🚨 THE ONE QUESTION A SESSION CLOSE MAY PUT ABOUT A NUMBER (BUD-378) ═══
 *
 * A jump the athlete DEMONSTRATED — two heavy sets in one session that agree with each other —
 * is believed without anybody being asked; that is the point of the feature. What is left is the
 * LONE OUTLIER: one set, nothing beside it, that would move the recorded max further than the
 * typo guard allows. It is exactly what the guard was written for and it is also, sometimes, the
 * truth, and the only party who can tell is the person who typed it.
 *
 * ⚠️ IT IS A QUESTION ABOUT AN ENTRY, NOT ABOUT AN ATHLETE'S STRENGTH. The sentence the client
 * puts on screen is "200 kg × 5 — is that right?", and where a unit error is plausible "200 kg —
 * kilos or pounds?". It is never "are you sure you lifted that": this app does not second-guess
 * the person doing the training.
 *
 * ⚠️ AND IT IS ASKED ONCE, AT THE END. Nothing in the service remembers that it was asked, so
 * "once" is the client's promise: a screen that re-reads this after a decline is told the same
 * thing again. Not answering is not a no — see `POST /limits/confirm`.
 */
export const maxConfirmationQuerySchema = z.object({
  /** The session that just closed. Someone else's log id matches nothing and asks nothing. */
  logId: z.uuid(),
});

export type MaxConfirmationQuery = z.infer<typeof maxConfirmationQuerySchema>;

export const maxConfirmationSchema = z.object({
  movement: z.string().min(1),
  /** The entry being asked about, exactly as the athlete typed it. */
  loadKg: z.number().positive(),
  reps: z.number().int().positive(),
  /** Where the recorded max lands if they say yes. */
  oneRepMaxKg: z.number().positive(),
  /**
   * Where it lands if they say nothing — the number the typo guard allows on its own, or null
   * where the clamped set does not beat what is already on file and nothing would be written.
   */
  withoutAnswerKg: z.number().positive().nullable(),
});

export type MaxConfirmationDTO = z.infer<typeof maxConfirmationSchema>;

/**
 * THE ANSWER, AND ONLY EVER THE YES.
 *
 * ⚠️ THERE IS NO `confirmed: false`, AND THAT IS THE DESIGN. A decline and a non-answer leave
 * the same thing behind — the capped number — and most non-answers are "the class started"
 * rather than "no", so a client that sent a no would be recording a decision the athlete did not
 * make. Saying yes is a call; saying nothing is not calling.
 */
export const confirmMaxSchema = z.object({
  logId: z.uuid(),
  movement: z.string().min(1).max(120),
});

export type ConfirmMaxPayload = z.infer<typeof confirmMaxSchema>;

/**
 * EVERYTHING THE PROFILE SCREEN NEEDS, IN ONE ROUND TRIP.
 *
 * Five tables behind one read, deliberately: on a phone a screen that fetches its five
 * sections separately is five trips queued behind one radio, and the last section lands
 * seconds after the first. The lists are unpaged because each is bounded by how many
 * movements a person has ever recorded — tens, not thousands — and a page control on a
 * list that short is a control nobody uses.
 */
export const athleteProfileSchema = z.object({
  body: athleteBodySchema.nullable(),
  ladders: z.array(athleteLadderSchema),
  limits: z.array(athleteLimitSchema),
  skills: z.array(athleteSkillSchema),
  restrictions: z.array(athleteRestrictionSchema),
  /**
   * THE WISHES TRAVEL WITH THE PROFILE (BUD-334) AND NOT ON THEIR OWN ENDPOINT. They are read on
   * every draw and rendered on a screen the athlete opens from the deck, and the argument this
   * schema already makes about five tables behind one read is the same one: a phone that fetches
   * a section separately is another trip queued behind one radio. Both lists are bounded by how
   * many movements a person bothers to have an opinion about, and by the thirty shapes.
   */
  wishes: z.array(athleteWishSchema),
  /** Empty means every shape — the `Any shape` pill. See `athleteShapeFilterSchema`. */
  shapeFilter: z.array(athleteShapeFilterSchema),
});

export type AthleteProfileDTO = z.infer<typeof athleteProfileSchema>;

/** The id in `/v1/athlete/limits/:id` and its siblings. */
export const athleteRowIdSchema = z.object({ id: z.uuid() });

export type AthleteRowIdParam = z.infer<typeof athleteRowIdSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   ANSWERING WHAT THE SESSION SCREEN OFFERED (BUD-396 · BUD-384)

   ═══ 🚨 WHY THE ANSWERS ARE THIS DOMAIN'S AND THE QUESTION IS THE SESSIONS DOMAIN'S ═══

   The notice is composed on `GET /v1/sessions/:id` because it is about the session in front
   of the athlete. What it is ABOUT lives in `athlete_working_offsets`, which this domain
   owns — and a repo is private to its domain, so the write belongs here and nowhere else.
   The screen therefore reads one feature and writes another, which is exactly the
   arrangement the barrel rule exists to produce: a query hook crosses, a service never does.
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * WHAT AN ATHLETE MAY SAY BACK, AND THE THIRD THING THEY MAY DO IS NOT IN HERE.
 *
 * 🚨 IGNORING IS NOT AN ANSWER AND HAS NO VALUE ON THIS ENUM. Most ignores are "the class
 * started" — writing one down as a decision would be inventing an answer nobody gave, so the
 * absence of a row IS the ignore and the notice simply comes back. Settled with the product
 * owner 2026-09-17.
 */
export const LIFT_ANSWERS = ["accepted", "declined"] as const;
export const liftAnswerSchema = z.enum(LIFT_ANSWERS);
export type LiftAnswer = (typeof LIFT_ANSWERS)[number];

/**
 * ANSWERING "THIS LIFT IS OWED A REAL TEST".
 *
 * `accepted` puts the lift at the front of the build-to-heavy queue — the draw reads it as
 * `AthleteProfile.retestAccepted` and programmes the measurement, which then writes the max
 * through BUD-377's path and by no other route. `declined` is FINAL at the weight the offer
 * was raised at: the same lift at a different working weight is a different question and is
 * asked again, which is the same stickiness `readProgression`'s ceiling verdict already has.
 */
export const answerRetestSchema = z.object({
  movement: z.string().min(1).max(120),
  answer: liftAnswerSchema,
});

export type AnswerRetestPayload = z.infer<typeof answerRetestSchema>;

/**
 * ANSWERING "LAST TIME DID NOT GO WELL — HERE IS A LIGHTER WEIGHT".
 *
 * ⚠️ THE DELOAD IS ALREADY APPLIED WHEN THIS IS ASKED, so `accepted` writes no weight: it
 * closes the offer and nothing else. `declined` RESTORES the working weight the athlete had
 * before it and records the refusal at that weight, so the rule does not take the same weight
 * down a second time. No count, no escalation, no second lever — somebody who declines and
 * keeps falling short simply repeats that weight, which the stall rule already handles by
 * reaching its exit ramp and offering a measurement instead.
 */
export const answerDeloadSchema = z.object({
  movement: z.string().min(1).max(120),
  answer: liftAnswerSchema,
});

export type AnswerDeloadPayload = z.infer<typeof answerDeloadSchema>;
